import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Image as ImageIcon, Send, Linkedin, Twitter, Instagram, Image, Pin, Plug,
  CheckCircle2, AlertCircle, Layers, RefreshCw, Download, ChevronLeft, ChevronRight, ShieldCheck, Crown, Copy, Key, ExternalLink, Trash2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const PLATFORMS = [
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "x", label: "X (Twitter)", icon: Twitter },
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "tiktok", label: "TikTok", icon: Image },
  { id: "pinterest", label: "Pinterest", icon: Pin },
];

const VISUAL_STYLES = {
  editorial: "minimalist editorial illustration, warm ivory background, single soft accent color, restrained typography, 1:1 square, India-modern aesthetic",
  cinematic: "moody cinematic photograph, deep contrast, single shaft of light, neutral palette, 1:1 square, Roger Deakins style",
  abstract: "abstract geometric illustration, ivory and ink palette with one saffron accent, generous negative space, 1:1 square, Saul Bass inspired",
  data: "clean data-visualization style infographic background, ivory paper, fine gridlines, soft serif numerals as decoration, 1:1 square",
  hand: "hand-drawn marker sketch on warm ivory paper, single ink color plus one soft wash, 1:1 square, Christoph Niemann style",
  pixar: "Pixar / Disney 3D animation still, warm cinematic lighting, soft global illumination, expressive characters with large eyes, vibrant but tasteful colour palette, shallow depth of field, 1:1 square, feature-film production quality",
  ghibli: "Studio Ghibli hand-painted animation cel, lush watercolour background, soft Miyazaki lighting, gentle pastel palette with warm sky tones, dreamy whimsical mood, 1:1 square, hand-drawn animation aesthetic in the style of Spirited Away and Howl's Moving Castle",
};

// Typography options offered to the user for carousel text rendering.
// `css` is used for both CSS `font-family` (preview) and Canvas `ctx.font` (download)
// so what they see in the preview is what comes out of the PNG.
const CAROUSEL_FONTS = [
  { id: "editorial", label: "Editorial · Lora (serif)",       css: "'Lora', Georgia, serif",                              meta: "Default · refined editorial" },
  { id: "modern",    label: "Modern · Manrope (sans)",        css: "'Manrope', 'Inter', system-ui, sans-serif",            meta: "Clean modern sans" },
  { id: "lucida",    label: "Lucida Sans Unicode",            css: "'Lucida Sans Unicode', 'Lucida Grande', sans-serif",   meta: "Crisp, technical" },
  { id: "times",     label: "Times New Roman",                css: "'Times New Roman', Times, serif",                      meta: "Classic newsprint" },
  { id: "impact",    label: "Impact (poster)",                css: "Impact, 'Haettenschweiler', 'Arial Black', sans-serif", meta: "Bold poster headlines" },
  { id: "comic",     label: "Comic Sans MS",                  css: "'Comic Sans MS', 'Comic Sans', cursive",               meta: "Friendly, casual" },
  { id: "georgia",   label: "Georgia (display)",              css: "Georgia, 'Times New Roman', serif",                    meta: "Warm display serif" },
  { id: "courier",   label: "Courier (typewriter)",           css: "'Courier New', Courier, monospace",                    meta: "Retro typewriter" },
  { id: "trebuchet", label: "Trebuchet MS",                   css: "'Trebuchet MS', 'Lucida Grande', sans-serif",          meta: "Rounded humanist" },
  { id: "brush",     label: "Brush Script (handwritten)",     css: "'Brush Script MT', 'Lucida Handwriting', 'Segoe Script', cursive", meta: "Handwritten flourish" },
];
const FONT_BY_ID = Object.fromEntries(CAROUSEL_FONTS.map((f) => [f.id, f]));

// ---------- text-layout positioning ----------
// The AI returns a `text_layout` per slide (one of 11 anchors). Both the LIVE PREVIEW
// and the EXPORT CANVAS need to honor it — otherwise the carousel always looks like a
// stamp of bottom-anchored text regardless of the model's art direction.
const TEXT_LAYOUTS = [
  "top-left", "top-center", "top-right",
  "center-left", "center", "center-right",
  "bottom-left", "bottom-center", "bottom-right",
  "left-vertical", "right-vertical",
];

// Tailwind class bundle for the IN-APP preview overlay.
// `vertical` is "left" or "right" for the rotated-text bookends; null for normal flow.
const previewLayout = (raw) => {
  const layout = TEXT_LAYOUTS.includes(raw) ? raw : "bottom-center";
  const M = {
    "top-left":      { justify: "justify-start",  items: "items-start",  textAlign: "text-left",   grad: "bg-gradient-to-b from-black/55 via-black/15 to-transparent" },
    "top-center":    { justify: "justify-start",  items: "items-center", textAlign: "text-center", grad: "bg-gradient-to-b from-black/55 via-black/15 to-transparent" },
    "top-right":     { justify: "justify-start",  items: "items-end",    textAlign: "text-right",  grad: "bg-gradient-to-b from-black/55 via-black/15 to-transparent" },
    "center-left":   { justify: "justify-center", items: "items-start",  textAlign: "text-left",   grad: "bg-gradient-to-r from-black/55 via-black/15 to-transparent" },
    "center":        { justify: "justify-center", items: "items-center", textAlign: "text-center", grad: "bg-black/35" },
    "center-right":  { justify: "justify-center", items: "items-end",    textAlign: "text-right",  grad: "bg-gradient-to-l from-black/55 via-black/15 to-transparent" },
    "bottom-left":   { justify: "justify-end",    items: "items-start",  textAlign: "text-left",   grad: "bg-gradient-to-t from-black/55 via-black/15 to-transparent" },
    "bottom-center": { justify: "justify-end",    items: "items-center", textAlign: "text-center", grad: "bg-gradient-to-t from-black/55 via-black/15 to-transparent" },
    "bottom-right":  { justify: "justify-end",    items: "items-end",    textAlign: "text-right",  grad: "bg-gradient-to-t from-black/55 via-black/15 to-transparent" },
    "left-vertical":  { vertical: "left",  grad: "bg-gradient-to-r from-black/55 via-black/15 to-transparent" },
    "right-vertical": { vertical: "right", grad: "bg-gradient-to-l from-black/55 via-black/15 to-transparent" },
  };
  return { layout, ...M[layout] };
};

// Canvas anchor + alignment + gradient geometry for the EXPORT renderer.
// Returns { hAnchorX, vAnchor, textAlign, gradient, vertical }.
// gradient is a 4-corner box (x0,y0,x1,y1) plus stops.
const canvasLayout = (raw, SIZE, PADDING) => {
  const layout = TEXT_LAYOUTS.includes(raw) ? raw : "bottom-center";
  const L = PADDING;
  const R = SIZE - PADDING;
  const T = PADDING + 56; // leave room for the slide-# badge in top-right
  const B = SIZE - PADDING;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  // Gradient direction follows where the text sits — darken THAT side, fade away.
  const G = {
    fromBottom: { x0: 0, y0: SIZE * 0.45, x1: 0, y1: SIZE,  stops: [[0,"rgba(0,0,0,0)"],[0.55,"rgba(0,0,0,0.38)"],[1,"rgba(0,0,0,0.82)"]] },
    fromTop:    { x0: 0, y0: 0,            x1: 0, y1: SIZE * 0.55, stops: [[0,"rgba(0,0,0,0.82)"],[0.45,"rgba(0,0,0,0.38)"],[1,"rgba(0,0,0,0)"]] },
    fromLeft:   { x0: 0, y0: 0,            x1: SIZE * 0.55, y1: 0,  stops: [[0,"rgba(0,0,0,0.78)"],[0.5,"rgba(0,0,0,0.32)"],[1,"rgba(0,0,0,0)"]] },
    fromRight:  { x0: SIZE * 0.45, y0: 0,  x1: SIZE, y1: 0,           stops: [[0,"rgba(0,0,0,0)"],[0.5,"rgba(0,0,0,0.32)"],[1,"rgba(0,0,0,0.78)"]] },
    centerWash: { x0: 0, y0: 0,            x1: 0, y1: SIZE,            stops: [[0,"rgba(0,0,0,0.18)"],[0.5,"rgba(0,0,0,0.45)"],[1,"rgba(0,0,0,0.18)"]] },
  };
  const M = {
    "top-left":      { x: L,  y: T, vAnchor: "top",    textAlign: "left",   gradient: G.fromTop },
    "top-center":    { x: CX, y: T, vAnchor: "top",    textAlign: "center", gradient: G.fromTop },
    "top-right":     { x: R,  y: T, vAnchor: "top",    textAlign: "right",  gradient: G.fromTop },
    "center-left":   { x: L,  y: CY, vAnchor: "middle", textAlign: "left",   gradient: G.fromLeft },
    "center":        { x: CX, y: CY, vAnchor: "middle", textAlign: "center", gradient: G.centerWash },
    "center-right":  { x: R,  y: CY, vAnchor: "middle", textAlign: "right",  gradient: G.fromRight },
    "bottom-left":   { x: L,  y: B, vAnchor: "bottom", textAlign: "left",   gradient: G.fromBottom },
    "bottom-center": { x: CX, y: B, vAnchor: "bottom", textAlign: "center", gradient: G.fromBottom },
    "bottom-right":  { x: R,  y: B, vAnchor: "bottom", textAlign: "right",  gradient: G.fromBottom },
    "left-vertical": { vertical: "left",  gradient: G.fromLeft },
    "right-vertical":{ vertical: "right", gradient: G.fromRight },
  };
  return { layout, ...M[layout] };
};

export default function ContentStudioPage() {
  const { user } = useAuth();
  const isStaff = user?.role === "recruiter" || user?.role === "admin";
  const isPlus = !!user?.is_subscribed;
  const canCreate = isStaff || isPlus;
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("post");

  // ── Single-post composer ──
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("thought-leader");
  const [platform, setPlatform] = useState("linkedin");
  const [length, setLength] = useState("medium");
  const [text, setText] = useState("");
  const [postCitations, setPostCitations] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [imagePrompt, setImagePrompt] = useState("");
  const [imageB64, setImageB64] = useState(null);
  const [genImg, setGenImg] = useState(false);
  // Provider-used badges — surface to user which model actually generated this output.
  const [postProviderUsed, setPostProviderUsed] = useState("");
  const [imageProviderUsed, setImageProviderUsed] = useState("");

  // ── Carousel composer ──
  const [carouselTopic, setCarouselTopic] = useState("");
  const [carouselTone, setCarouselTone] = useState("thought-leader");
  const [carouselSlideCount, setCarouselSlideCount] = useState(5);
  const [carouselStyle, setCarouselStyle] = useState("editorial");
  const [carouselPlatform, setCarouselPlatform] = useState("linkedin");
  const [carouselGenerating, setCarouselGenerating] = useState(false);
  const [carouselCaption, setCarouselCaption] = useState("");
  const [carouselSlides, setCarouselSlides] = useState([]); // [{kind,headline,body,image_prompt,image_base64}]
  const [carouselCitations, setCarouselCitations] = useState([]);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [retryingSlide, setRetryingSlide] = useState(null);
  const [carouselFont, setCarouselFont] = useState("editorial");
  const [carouselTextProviderUsed, setCarouselTextProviderUsed] = useState("");
  const [carouselImageProviderUsed, setCarouselImageProviderUsed] = useState("");
  // Bake-text-into-image mode: default ON (gpt-image-1 renders typography natively, far more premium look).
  // Auto-disable on Gemini fallback (its typography rendering is unreliable).
  const [bakeTextInImage, setBakeTextInImage] = useState(false);

  // ── Publishing ──
  const [selected, setSelected] = useState(["linkedin"]);
  const [posting, setPosting] = useState(false);
  const [status, setStatus] = useState({});

  // ── BYOK (Bring Your Own Key) — user-connected AI accounts ──
  const [aiAccounts, setAiAccounts] = useState([]); // [{provider,label,kind,connected,masked,...}]
  const [byokOpenProvider, setByokOpenProvider] = useState(null); // which connect modal is open
  const [byokKeyInput, setByokKeyInput] = useState("");
  const [byokSaving, setByokSaving] = useState(false);
  const [textProvider, setTextProvider] = useState("managed"); // "managed" | openai | anthropic | gemini | perplexity
  const [imageProvider, setImageProvider] = useState("managed"); // "managed" | openai | gemini

  const loadAiAccounts = async () => {
    try {
      const r = await api.get("/byok");
      setAiAccounts(r.data?.providers || []);
    } catch (e) { console.error("byok load failed:", e); /* not Plus or transient — leave empty */ }
  };

  const connectAi = async (provider) => {
    if (!byokKeyInput.trim()) {
      toast.error("Please paste your API key first");
      return;
    }
    setByokSaving(true);
    try {
      await api.post("/byok", { provider, api_key: byokKeyInput.trim() });
      toast.success(`${provider} connected · key verified live`);
      setByokOpenProvider(null);
      setByokKeyInput("");
      await loadAiAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to connect");
    } finally {
      setByokSaving(false);
    }
  };

  const disconnectAi = async (provider) => {
    try {
      await api.delete(`/byok/${provider}`);
      toast.success(`${provider} disconnected`);
      // If user had selected this provider, fall back to managed
      if (textProvider === provider) setTextProvider("managed");
      if (imageProvider === provider) setImageProvider("managed");
      await loadAiAccounts();
    } catch { toast.error("Failed to disconnect"); }
  };

  // Live-test a connected BYOK key. Hits the provider's own API with a 1-token request
  // and surfaces latency + any rate-limit / quota headers the provider returned.
  const [byokTesting, setByokTesting] = useState(null); // provider id currently being tested
  const testAi = async (provider) => {
    setByokTesting(provider);
    try {
      const r = await api.post(`/byok/${provider}/probe`);
      if (r.data?.ok) {
        toast.success(`${provider} · live · ${r.data.latency_ms}ms`);
      } else {
        toast.error(`${provider} test failed: ${r.data?.error || "unknown"}`);
      }
      // Refresh so the inline panel shows the new last_probe payload
      await loadAiAccounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${provider} probe failed`);
    } finally {
      setByokTesting(null);
    }
  };

  // Text-model picker options: managed (default) + any connected text-capable BYOK accounts
  const textProviderOptions = [
    { id: "managed", label: "Default · Claude Sonnet 4.6 (managed)", connected: true },
    ...aiAccounts.filter(a => a.kind.includes("text") && a.connected).map(a => ({
      id: a.provider, label: `Your ${a.label}`, connected: true,
    })),
  ];
  const imageProviderOptions = [
    { id: "managed", label: "Default · gpt-image-1 (managed)", connected: true },
    ...aiAccounts.filter(a => a.kind.includes("image") && a.connected).map(a => ({
      id: a.provider, label: `Your ${a.label}`, connected: true,
    })),
  ];

  const loadStatus = async () => {
    try {
      const r = await api.get("/social/status");
      setStatus(r.data || {});
    } catch (e) { console.error("social status load failed:", e); /* connection bar degrades silently */ }
  };
  useEffect(() => { loadStatus(); loadAiAccounts(); }, []);

  useEffect(() => {
    const s = params.get("social");
    if (s === "connected") toast.success(`${params.get("provider")} connected. You can now publish directly.`);
    if (s === "error") toast.error(`Could not connect ${params.get("provider")} — ${params.get("reason") || "unknown"}`);
    if (s) { params.delete("social"); params.delete("provider"); params.delete("reason"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const connect = async (pid) => {
    try {
      const r = await api.get(`/social/${pid}/connect`);
      if (r.data?.authorize_url) window.location.href = r.data.authorize_url;
      else toast.error("Could not start OAuth.");
    } catch (e) { toast.error(e?.response?.data?.detail || `${pid} not configured on server`); }
  };
  const disconnect = async (pid) => {
    try { await api.delete(`/social/${pid}`); toast.success(`Disconnected ${pid}.`); loadStatus(); }
    catch { toast.error("Disconnect failed"); }
  };

  // ── Single-post handlers ──
  const generate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setPostCitations([]);
    setPostProviderUsed("");
    try {
      const r = await api.post("/content/generate", {
        topic, tone, platform, length,
        text_provider: textProvider === "managed" ? null : textProvider,
      });
      setText(r.data.text);
      setPostCitations(r.data.citations || []);
      setPostProviderUsed(r.data.provider_used || "");
    } catch { toast.error("Generation failed"); }
    finally { setGenerating(false); }
  };
  const generateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGenImg(true);
    setImageProviderUsed("");
    try {
      const r = await api.post("/content/image", {
        prompt: imagePrompt,
        image_provider: imageProvider === "managed" ? null : imageProvider,
      });
      setImageB64(r.data.image_base64);
      setImageProviderUsed(r.data.provider_used || "");
      toast.success("Image ready");
    } catch (e) { toast.error(e?.response?.data?.detail || "Image gen failed"); }
    finally { setGenImg(false); }
  };

  // ── Carousel handlers ──
  const generateCarousel = async () => {
    if (!carouselTopic.trim()) return;
    setCarouselGenerating(true);
    setCarouselSlides([]);
    setCarouselCaption("");
    setCarouselCitations([]);
    setCarouselIdx(0);
    try {
      // Step 1 — fast script call (~5s). Returns caption + slide texts + per-slide text_layout + image_prompts.
      const scriptR = await api.post(
        "/content/carousel/script",
        {
          topic: carouselTopic,
          tone: carouselTone,
          num_slides: carouselSlideCount,
          visual_style: VISUAL_STYLES[carouselStyle],
          platform: carouselPlatform,
          bake_text: bakeTextInImage,
          image_provider: imageProvider === "managed" ? null : imageProvider,
          text_provider: textProvider === "managed" ? null : textProvider,
        },
        { timeout: 75000 },
      );
      setCarouselCaption(scriptR.data.hook_caption || "");
      const scripted = scriptR.data.slides || [];
      setCarouselCitations(scriptR.data.citations || []);
      setCarouselTextProviderUsed(scriptR.data.text_provider_used || "");
      setCarouselImageProviderUsed("");
      // Render immediately with placeholders so the user sees the story arc.
      setCarouselSlides(scripted);

      // Step 2 — fetch each slide image one-by-one (sequential, ~12-18s each).
      // Each call lives inside the 60s edge-proxy budget. Failures don't abort siblings.
      for (let i = 0; i < scripted.length; i++) {
        try {
          const imgR = await api.post(
            "/content/carousel/slide-image",
            {
              prompt: scripted[i].image_prompt,
              image_provider: imageProvider === "managed" ? null : imageProvider,
            },
            { timeout: 50000 },
          );
          setCarouselSlides((cur) =>
            cur.map((s, j) => (j === i ? { ...s, image_base64: imgR.data.image_base64 } : s)),
          );
          // The image provider is the same for every slide — capture it from the first successful one.
          if (i === 0 && imgR.data?.provider_used) setCarouselImageProviderUsed(imgR.data.provider_used);
        } catch (e) {
          // Mark this slide as failed but continue. UI exposes per-slide retry.
          /* eslint-disable-next-line no-console */
          console.warn(`Slide ${i + 1} image failed`, e);
        }
      }
      toast.success("Carousel ready — review each slide before publishing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Carousel generation failed");
    } finally {
      setCarouselGenerating(false);
    }
  };

  const retrySlideImage = async (idx) => {
    const slide = carouselSlides[idx];
    if (!slide) return;
    setRetryingSlide(idx);
    try {
      const r = await api.post("/content/carousel/slide-image", {
        prompt: slide.image_prompt,
        headline: slide.headline,
        body: slide.body,
        visual_style: VISUAL_STYLES[carouselStyle],
        text_layout: slide.text_layout || "bottom-center",
        bake_text: bakeTextInImage,
        image_provider: imageProvider === "managed" ? null : imageProvider,
      });
      setCarouselSlides((slides) => slides.map((s, i) => (i === idx ? { ...s, image_base64: r.data.image_base64 } : s)));
      if (r.data?.provider_used) setCarouselImageProviderUsed(r.data.provider_used);
      toast.success(`Slide ${idx + 1} regenerated`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetryingSlide(null);
    }
  };

  const editSlide = (idx, field, value) => {
    setCarouselSlides((slides) => slides.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const copySlideText = async (slide, idx) => {
    // Plain headline + body — exactly what users paste into LinkedIn/IG native composers.
    const txt = [slide.headline, slide.body].filter(Boolean).join("\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`Slide ${idx + 1} text copied`);
    } catch {
      // Fallback for non-secure contexts: use a temporary textarea
      const ta = document.createElement("textarea");
      ta.value = txt;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`Slide ${idx + 1} text copied`);
    }
  };

  const copyAllSlideText = async () => {
    // Bulk export: numbered, double-newline separated, ready for LinkedIn carousel walkthrough posts.
    const txt = carouselSlides
      .map((s, i) => `Slide ${i + 1}\n${s.headline}${s.body ? "\n" + s.body : ""}`)
      .join("\n\n———\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`All ${carouselSlides.length} slides copied`);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success(`All ${carouselSlides.length} slides copied`);
    }
  };

  const copyLinkedInBundle = async () => {
    // Complete LinkedIn carousel post: caption first, then each slide formatted
    // as a "↓ Slide N" walkthrough — one paste, ready to publish.
    const parts = [];
    if (carouselCaption.trim()) parts.push(carouselCaption.trim());
    carouselSlides.forEach((s, i) => {
      const slideBlock = [`↓ Slide ${i + 1}: ${s.headline || ""}`.trim()];
      if (s.body) slideBlock.push(s.body);
      parts.push(slideBlock.join("\n"));
    });
    const txt = parts.join("\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("LinkedIn post bundle copied — paste & publish");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("LinkedIn post bundle copied — paste & publish");
    }
  };

  const downloadCarousel = async () => {
    if (!carouselSlides.length) return;

    const fontFamily = (FONT_BY_ID[carouselFont] || FONT_BY_ID.editorial).css;
    // When text is already baked into the image by the model, downloading direct
    // gives the user the premium look without our canvas overlay re-painting it.
    if (bakeTextInImage) {
      for (let i = 0; i < carouselSlides.length; i++) {
        const slide = carouselSlides[i];
        if (!slide.image_base64) continue;
        const src = slide.image_base64.startsWith("data:")
          ? slide.image_base64
          : `data:image/png;base64,${slide.image_base64}`;
        const a = document.createElement("a");
        a.href = src;
        a.download = `karm-slide-${i + 1}.png`;
        a.click();
        await new Promise((r) => setTimeout(r, 200));
      }
      toast.success(`Downloaded ${carouselSlides.length} slides (text baked into image)`);
      return;
    }

    // Legacy / Gemini fallback path — overlay text via Canvas. Used when bake_text is OFF.
    // Render each slide as a 1080×1080 PNG with the headline + body burned in,
    // matching the in-app preview overlay so "what you see is what you download."
    const renderSlide = (slide, index, total) =>
      new Promise((resolve) => {
        const SIZE = 1080;
        const PADDING = 64;
        const canvas = document.createElement("canvas");
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");

        const drawText = () => {
          const L = canvasLayout(slide.text_layout, SIZE, PADDING);

          // ── Directional gradient: darken WHERE the text sits, fade out elsewhere ──
          const g = L.gradient;
          const grad = ctx.createLinearGradient(g.x0, g.y0, g.x1, g.y1);
          for (const [stop, color] of g.stops) grad.addColorStop(stop, color);
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, SIZE, SIZE);

          // ── Slide number badge: TOP-RIGHT, isolated from body copy ──
          // Pill background → ensures legibility on any image, prevents collision with headline.
          const badgeText = `${index + 1} / ${total}`;
          ctx.font = `600 22px ${fontFamily}`;
          const badgeW = ctx.measureText(badgeText).width + 28;
          const badgeH = 36;
          const badgeX = SIZE - PADDING - badgeW;
          const badgeY = PADDING;
          ctx.fillStyle = "rgba(0,0,0,0.55)";
          ctx.beginPath();
          if (ctx.roundRect) {
            ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 18);
          } else {
            ctx.rect(badgeX, badgeY, badgeW, badgeH);
          }
          ctx.fill();
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(badgeText, badgeX + badgeW / 2, badgeY + badgeH / 2 + 1);

          // ── Headline + body sizing ──
          const maxWidth = SIZE - PADDING * 2;
          const headlineSize = 60;
          const headlineLH = 70;
          const bodySize = 30;
          const bodyLH = 38;

          // ── Vertical-band layouts (rotated 90°) — handled specially ──
          if (L.vertical) {
            // Combined headline+body, rotated, drawn along the vertical band.
            const combined = [slide.headline || "", slide.body || ""].filter(Boolean).join(" — ");
            ctx.save();
            // Anchor in the middle of the canvas, then rotate.
            const bandX = L.vertical === "left" ? PADDING + 8 : SIZE - PADDING - 8;
            ctx.translate(bandX, SIZE / 2);
            ctx.rotate(L.vertical === "left" ? -Math.PI / 2 : Math.PI / 2);
            ctx.fillStyle = "#FFFFFF";
            ctx.font = `600 ${headlineSize}px ${fontFamily}`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(combined, 0, 0, SIZE - PADDING * 2);
            ctx.restore();

            canvas.toBlob(
              (blob) => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `karm-slide-${index + 1}.png`;
                a.click();
                URL.revokeObjectURL(a.href);
                resolve();
              },
              "image/png",
              0.95,
            );
            return;
          }

          // ── Pre-compute the line wraps so we know the block's total height ──
          ctx.textAlign = L.textAlign;
          ctx.textBaseline = "alphabetic";
          ctx.font = `italic 400 ${bodySize}px ${fontFamily}`;
          const bodyLines = (slide.body || "").trim()
            ? wrapTextLines(ctx, slide.body, maxWidth)
            : [];
          const bodyHeight = bodyLines.length * bodyLH;

          ctx.font = `600 ${headlineSize}px ${fontFamily}`;
          const headlineLines = wrapTextLines(ctx, slide.headline || "", maxWidth);
          const headlineHeight = headlineLines.length * headlineLH;

          const gap = 18;
          const totalH = headlineHeight + (bodyHeight ? gap + bodyHeight : 0);

          // ── Compute headline + body Y based on vAnchor ──
          // L.x is the anchor X (varies by left/center/right via textAlign), L.y is the anchor Y.
          let headlineStartY;
          if (L.vAnchor === "top") {
            headlineStartY = L.y + headlineLH * 0.85;
          } else if (L.vAnchor === "middle") {
            headlineStartY = L.y - totalH / 2 + headlineLH * 0.85;
          } else { // bottom
            headlineStartY = L.y - totalH + headlineLH * 0.85;
          }
          const bodyStartY = headlineStartY + (headlineLines.length - 1) * headlineLH + gap + bodyLH * 0.85;

          // ── Draw headline (white, bold) ──
          ctx.fillStyle = "#FFFFFF";
          ctx.font = `600 ${headlineSize}px ${fontFamily}`;
          for (let i = 0; i < headlineLines.length; i++) {
            ctx.fillText(headlineLines[i], L.x, headlineStartY + i * headlineLH);
          }
          // ── Draw body (italic, lighter) ──
          ctx.fillStyle = "rgba(255,255,255,0.92)";
          ctx.font = `italic 400 ${bodySize}px ${fontFamily}`;
          for (let i = 0; i < bodyLines.length; i++) {
            ctx.fillText(bodyLines[i], L.x, bodyStartY + i * bodyLH);
          }

          canvas.toBlob(
            (blob) => {
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `karm-slide-${index + 1}.png`;
              a.click();
              URL.revokeObjectURL(a.href);
              resolve();
            },
            "image/png",
            0.95,
          );
        };

        if (slide.image_base64) {
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            // cover-fit the source image into the square canvas
            const s = Math.min(img.width, img.height);
            const sx = (img.width - s) / 2;
            const sy = (img.height - s) / 2;
            ctx.drawImage(img, sx, sy, s, s, 0, 0, SIZE, SIZE);
            drawText();
          };
          img.onerror = () => {
            // Image failed to load — render a paper-ivory background card so the slide is still usable.
            ctx.fillStyle = "#FBF8F1";
            ctx.fillRect(0, 0, SIZE, SIZE);
            ctx.fillStyle = "#1A1208";
            drawText();
          };
          img.src = slide.image_base64.startsWith("data:")
            ? slide.image_base64
            : `data:image/png;base64,${slide.image_base64}`;
        } else {
          // No image yet — render text-only card on ivory
          ctx.fillStyle = "#FBF8F1";
          ctx.fillRect(0, 0, SIZE, SIZE);
          drawText();
        }
      });

    for (let i = 0; i < carouselSlides.length; i++) {
      await renderSlide(carouselSlides[i], i, carouselSlides.length);
      await new Promise((r) => setTimeout(r, 250));
    }
    toast.success(`Downloaded ${carouselSlides.length} slides with text baked in`);
  };

  // Wraps `text` into an array of lines that fit within `maxWidth`. Caller is
  // expected to have already set `ctx.font` to the measurement font.
  function wrapTextLines(ctx, text, maxWidth) {
    const words = (text || "").split(/\s+/).filter(Boolean);
    const lines = [];
    let current = "";
    for (const w of words) {
      const test = current ? current + " " + w : w;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = w;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  const togglePlatform = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const post = async () => {
    const bodyText = tab === "carousel" ? carouselCaption : text;
    const firstImg = tab === "carousel" ? carouselSlides[0]?.image_base64 : imageB64;
    if (!bodyText.trim() || selected.length === 0) return;
    setPosting(true);
    try {
      const r = await api.post("/content/post", {
        text: bodyText,
        platforms: selected,
        image_base64: firstImg,
      });
      const results = r.data.results || [];
      const linkedinIntent = results.find((x) => x.platform === "linkedin" && x.share_url);
      if (linkedinIntent) window.open(linkedinIntent.share_url, "_blank");
      const real = results.filter((x) => x.real && x.status === "published");
      const queued = results.filter((x) => x.real && x.status === "queued");
      const mocked = results.filter((x) => x.mock);
      const msgs = [];
      if (real.length) msgs.push(`Published live to ${real.map((r2) => r2.platform).join(", ")}`);
      if (queued.length) msgs.push(`Queued on ${queued.map((r2) => r2.platform).join(", ")}`);
      if (linkedinIntent) msgs.push("LinkedIn share dialog opened");
      if (mocked.length) msgs.push(`Simulated for ${mocked.map((r2) => r2.platform).join(", ")}`);
      if (tab === "carousel" && carouselSlides.length > 1) {
        msgs.push(`+ ${carouselSlides.length - 1} additional slides downloaded for manual upload`);
        // Trigger download of remaining slides as user will need to attach them manually
        await downloadCarousel();
      }
      toast.success(msgs.join(" · ") || "Posted");
    } catch { toast.error("Post failed"); }
    finally { setPosting(false); }
  };

  const statusOf = (pid) => status[pid] || {};

  if (!canCreate) {
    return (
      <div className="container py-16 max-w-3xl" data-testid="content-studio-paywall">
        <p className="eyebrow mb-2 text-accent inline-flex items-center gap-1">
          <Crown className="h-3 w-3" /> KARM Plus
        </p>
        <h1 className="display-2 mb-4">Content Studio is a Plus feature.</h1>
        <p className="text-base text-muted-foreground mb-8 max-w-2xl">
          Generate researched LinkedIn posts and 5-slide carousels with verifiable facts &amp; citations, plus
          editorial-grade images — on any topic you choose. Unlock with KARM Plus.
        </p>
        <div className="vital-card-static p-6 mb-6">
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> Single-post composer with citations (Claude Sonnet 4.6)</li>
            <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> 5-slide carousels with cohesive visuals (gpt-image-1)</li>
            <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> Direct publish to LinkedIn, X, Instagram, TikTok, Pinterest</li>
            <li className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> Application Concierge for every job you apply to</li>
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild size="lg" data-testid="paywall-upgrade-btn">
            <Link to="/pricing">
              <Crown className="mr-2 h-4 w-4" />
              Upgrade · $5/mo · cancel anytime
            </Link>
          </Button>
          <Button asChild variant="ghost" size="lg" data-testid="paywall-back-btn">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-10 max-w-6xl">
      <p className="eyebrow mb-1 text-accent">AI · Content Studio</p>
      <h1 className="display-2 mb-2">Build credibility. One piece at a time.</h1>
      <p className="text-xs text-muted-foreground mb-8" data-testid="content-studio-attribution">
        Text · Claude Sonnet 4.6 (researched, facts &amp; figures) &nbsp;·&nbsp; Images · OpenAI gpt-image-1
      </p>

      {/* Connection bar */}
      <section className="vital-card-static p-5 mb-8" data-testid="social-connections">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold inline-flex items-center gap-2"><Plug className="h-4 w-4" />Connected accounts</h2>
          <span className="text-xs text-muted-foreground">Real OAuth · tokens stored encrypted</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {PLATFORMS.map((p) => {
            const s = statusOf(p.id);
            const Icon = p.icon;
            const cls = s.connected ? "border-emerald-500/40 bg-emerald-500/5" : s.configured ? "border-border" : "border-border/50 opacity-60";
            return (
              <div key={p.id} className={`rounded-sm border p-3 ${cls}`} data-testid={`connection-${p.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <Icon className="h-4 w-4" />
                  {s.connected ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : s.configured ? <AlertCircle className="h-3 w-3 text-muted-foreground" /> : null}
                </div>
                <div className="text-sm font-semibold">{p.label}</div>
                <div className="text-[11px] text-muted-foreground mb-2">
                  {s.connected ? `Linked${s.handle ? ` · @${s.handle}` : ""}` : s.configured ? "Available" : "Not configured"}
                </div>
                {s.connected ? (
                  <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => disconnect(p.id)} data-testid={`disconnect-${p.id}`}>
                    Disconnect
                  </Button>
                ) : s.configured ? (
                  <Button size="sm" className="w-full h-7 text-xs" onClick={() => connect(p.id)} data-testid={`connect-${p.id}`}>
                    Connect
                  </Button>
                ) : (
                  <Button size="sm" disabled variant="ghost" className="w-full h-7 text-xs" data-testid={`disabled-${p.id}`}>
                    Admin to enable
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* AI Accounts (BYOK) — user-supplied LLM API keys for direct calls */}
      <section className="vital-card-static p-5 mb-8" data-testid="ai-accounts">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-bold inline-flex items-center gap-2"><Key className="h-4 w-4" />AI accounts (BYOK)</h2>
          <span className="text-xs text-muted-foreground">Encrypted at rest · used only when you pick them</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Bring your own API keys to use your personal ChatGPT, Claude, Gemini or Perplexity credits instead of KARM&apos;s managed quota. Optional — defaults keep working.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {aiAccounts.map((a) => {
            const cls = a.connected ? "border-emerald-500/40 bg-emerald-500/5" : "border-border";
            const probe = a.last_probe || {};
            const probeAt = probe.at ? new Date(probe.at).toLocaleString() : null;
            // Pick a couple of headline quota fields from the provider's response.
            const q = probe.quota || {};
            const quotaLine = (() => {
              if (q.remaining_requests || q.requests_remaining) {
                const rem = q.remaining_requests || q.requests_remaining;
                const lim = q.limit_requests || q.requests_limit;
                return `${rem}${lim ? ` / ${lim}` : ""} req remaining`;
              }
              if (q.remaining_tokens || q.tokens_remaining) {
                const rem = q.remaining_tokens || q.tokens_remaining;
                const lim = q.limit_tokens || q.tokens_limit;
                return `${rem}${lim ? ` / ${lim}` : ""} tokens remaining`;
              }
              return null;
            })();
            return (
              <div key={a.provider} className={`rounded-sm border p-3 ${cls}`} data-testid={`ai-account-${a.provider}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{a.kind}</span>
                  {a.connected ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : null}
                </div>
                <div className="text-sm font-semibold">{a.label}</div>
                <div className="text-[11px] text-muted-foreground mb-2 truncate" title={a.connected ? `Key: ${a.masked}` : a.prefix_hint}>
                  {a.connected ? <>Key: <code className="text-[10px]">{a.masked}</code></> : <>Format: <code className="text-[10px]">{a.prefix_hint}</code></>}
                </div>
                {a.connected && probeAt && (
                  <div
                    className={`mb-2 p-1.5 rounded-sm border text-[10px] leading-relaxed ${
                      probe.ok ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300"
                               : "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300"
                    }`}
                    data-testid={`ai-probe-status-${a.provider}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">
                        {probe.ok ? `✓ Live · ${probe.latency_ms}ms` : "✗ Failed"}
                      </span>
                      <span className="opacity-60 truncate" title={probeAt}>{probeAt}</span>
                    </div>
                    {!probe.ok && probe.error && (
                      <div className="mt-0.5 opacity-90 break-words">{probe.error}</div>
                    )}
                    {quotaLine && (
                      <div className="mt-0.5 opacity-80">📊 {quotaLine}</div>
                    )}
                  </div>
                )}
                {a.connected ? (
                  <div className="grid grid-cols-2 gap-1">
                    <Button
                      variant="outline" size="sm"
                      className="h-7 text-xs px-1"
                      onClick={() => testAi(a.provider)}
                      disabled={byokTesting === a.provider}
                      data-testid={`ai-test-${a.provider}`}
                    >
                      {byokTesting === a.provider ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><CheckCircle2 className="h-3 w-3 mr-1" /> Test</>
                      )}
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      className="h-7 text-xs px-1"
                      onClick={() => disconnectAi(a.provider)}
                      data-testid={`ai-disconnect-${a.provider}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm" className="w-full h-7 text-xs"
                    onClick={() => { setByokKeyInput(""); setByokOpenProvider(a.provider); }}
                    data-testid={`ai-connect-${a.provider}`}
                  >
                    Connect
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* BYOK connect modal */}
      <Dialog open={!!byokOpenProvider} onOpenChange={(open) => { if (!open) { setByokOpenProvider(null); setByokKeyInput(""); } }}>
        <DialogContent data-testid="byok-modal">
          {byokOpenProvider && (() => {
            const p = aiAccounts.find(x => x.provider === byokOpenProvider) || {};
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Connect {p.label}</DialogTitle>
                  <DialogDescription>
                    It&apos;s encrypted server-side and only used when you select this provider on the generate form. We verify the key live before saving — bad keys are rejected.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <Label htmlFor="byok-key-input">API key</Label>
                  <Input
                    id="byok-key-input"
                    type="password"
                    autoComplete="off"
                    spellCheck={false}
                    value={byokKeyInput}
                    onChange={(e) => setByokKeyInput(e.target.value)}
                    placeholder={p.prefix_hint || "Paste your API key"}
                    data-testid="byok-key-input"
                  />
                  <a
                    href={p.signup} target="_blank" rel="noreferrer"
                    className="text-xs inline-flex items-center gap-1 text-accent hover:underline"
                    data-testid="byok-signup-link"
                  >
                    <ExternalLink className="h-3 w-3" /> Get a {p.label?.split('·')[0]?.trim() || p.label} key
                  </a>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => { setByokOpenProvider(null); setByokKeyInput(""); }} data-testid="byok-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={() => connectAi(byokOpenProvider)}
                    disabled={byokSaving || !byokKeyInput.trim()}
                    data-testid="byok-save"
                  >
                    {byokSaving ? "Verifying & saving…" : "Save & verify"}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger value="post" data-testid="tab-single-post" className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Single post
          </TabsTrigger>
          <TabsTrigger value="carousel" data-testid="tab-carousel" className="inline-flex items-center gap-2">
            <Layers className="h-4 w-4" /> Carousel (multi-slide)
          </TabsTrigger>
        </TabsList>

        {/* ─────────────── SINGLE-POST TAB ─────────────── */}
        <TabsContent value="post">
          <div className="grid lg:grid-cols-2 gap-6">
            <section className="creative-zone p-6">
              <h2 className="text-lg font-bold mb-4 inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" />AI Composer</h2>
              <div className="space-y-3">
                <div>
                  <Label>Topic / angle</Label>
                  <Textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Five behaviours when you've seen a lot of office politics; my framework for technical interviews; what cycling taught me about deep work…" data-testid="content-topic" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Platform</Label>
                    <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="content-platform">
                      {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Tone</Label>
                    <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="content-tone">
                      <option value="thought-leader">Thought leader</option>
                      <option value="professional">Professional</option>
                      <option value="inspirational">Inspirational</option>
                      <option value="casual">Casual</option>
                    </select>
                  </div>
                  <div>
                    <Label>Length</Label>
                    <select value={length} onChange={(e) => setLength(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="content-length">
                      <option value="short">Short</option>
                      <option value="medium">Medium</option>
                      <option value="long">Long</option>
                    </select>
                  </div>
                </div>

                {/* Model pickers — let users route this generation through the managed key or any connected BYOK provider */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="inline-flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Text model
                    </Label>
                    <select
                      value={textProvider}
                      onChange={(e) => setTextProvider(e.target.value)}
                      className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm"
                      data-testid="content-text-provider"
                    >
                      {textProviderOptions.map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="inline-flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" /> Image model
                    </Label>
                    <select
                      value={imageProvider}
                      onChange={(e) => setImageProvider(e.target.value)}
                      className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm"
                      data-testid="content-image-provider"
                    >
                      {imageProviderOptions.map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground -mt-1">
                  {aiAccounts.some(a => a.connected)
                    ? "Pick \"Your …\" to bill the generation to your BYOK account; otherwise the managed Universal Key handles it."
                    : "Tip: connect an AI account above to use your own credits."}
                </p>
                <Button onClick={generate} disabled={generating || !topic.trim()} className="w-full h-11" data-testid="content-generate-btn">
                  {generating ? "Drafting…" : "Generate post"}
                </Button>
              </div>

              <div className="mt-6">
                <Tabs defaultValue="text">
                  <TabsList className="grid grid-cols-2">
                    <TabsTrigger value="text" data-testid="tab-content-text">Text</TabsTrigger>
                    <TabsTrigger value="image" data-testid="tab-content-image">Image</TabsTrigger>
                  </TabsList>
                  <TabsContent value="text">
                    <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder="Your post will appear here. Edit freely." data-testid="content-output" className="mt-3" />
                    {postProviderUsed && (
                      <p className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="post-provider-used">
                        <Sparkles className="h-3 w-3 text-accent" />
                        <span>Generated by <strong className="text-foreground">{postProviderUsed}</strong></span>
                      </p>
                    )}
                  </TabsContent>
                  <TabsContent value="image">
                    <div className="space-y-3 mt-3">
                      <Input value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} placeholder="Describe what to SHOW (not a title) — e.g. 'a woman in lotus pose, warm morning light, soft ivory studio'…" data-testid="image-prompt" />
                      <Button onClick={generateImage} disabled={genImg || !imagePrompt.trim()} className="w-full" data-testid="image-generate-btn">
                        <ImageIcon className="mr-2 h-4 w-4" />{genImg ? "Generating…" : "Generate image"}
                      </Button>
                      {imageB64 && (
                        <img src={imageB64.startsWith("data:") ? imageB64 : `data:image/png;base64,${imageB64}`} alt="generated" className="rounded-sm border border-border w-full" data-testid="generated-image" />
                      )}
                      {imageProviderUsed && (
                        <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="image-provider-used">
                          <ImageIcon className="h-3 w-3 text-accent" />
                          <span>Generated by <strong className="text-foreground">{imageProviderUsed}</strong></span>
                        </p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </section>

            <SinglePostPreview text={text} imageB64={imageB64} selected={selected} togglePlatform={togglePlatform} status={status} post={post} posting={posting} citations={postCitations} />
          </div>
        </TabsContent>

        {/* ─────────────── CAROUSEL TAB ─────────────── */}
        <TabsContent value="carousel">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
            <section className="creative-zone p-6" data-testid="carousel-composer">
              <h2 className="text-lg font-bold mb-4 inline-flex items-center gap-2">
                <Layers className="h-4 w-4 text-accent" /> Carousel composer
              </h2>
              <div className="space-y-3">
                <div>
                  <Label>Topic / story arc</Label>
                  <Textarea
                    rows={3}
                    value={carouselTopic}
                    onChange={(e) => setCarouselTopic(e.target.value)}
                    placeholder="5 behaviours when you've seen a lot of office politics; what cycling taught me about deep work; a framework for choosing between IC and EM tracks…"
                    data-testid="carousel-topic"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Slides</Label>
                    <select value={carouselSlideCount} onChange={(e) => setCarouselSlideCount(parseInt(e.target.value))} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="carousel-slide-count">
                      {[3, 4, 5, 6, 7, 8, 10].map((n) => <option key={n} value={n}>{n} slides</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Tone</Label>
                    <select value={carouselTone} onChange={(e) => setCarouselTone(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="carousel-tone">
                      <option value="thought-leader">Thought leader</option>
                      <option value="professional">Professional</option>
                      <option value="inspirational">Inspirational</option>
                      <option value="casual">Casual</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Visual style</Label>
                    <select value={carouselStyle} onChange={(e) => setCarouselStyle(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="carousel-style">
                      <option value="editorial">Editorial illustration</option>
                      <option value="cinematic">Cinematic photograph</option>
                      <option value="abstract">Abstract geometric</option>
                      <option value="data">Data infographic</option>
                      <option value="hand">Hand-drawn sketch</option>
                      <option value="pixar">Disney · Pixar 3D animation</option>
                      <option value="ghibli">Studio Ghibli animation</option>
                    </select>
                  </div>
                  <div>
                    <Label>Platform</Label>
                    <select value={carouselPlatform} onChange={(e) => setCarouselPlatform(e.target.value)} className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm" data-testid="carousel-platform">
                      <option value="linkedin">LinkedIn</option>
                      <option value="instagram">Instagram</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Typography</Label>
                  <select
                    value={carouselFont}
                    onChange={(e) => setCarouselFont(e.target.value)}
                    className="w-full mt-1 h-10 px-3 rounded-sm bg-background border border-border text-sm"
                    data-testid="carousel-font"
                    style={{ fontFamily: (FONT_BY_ID[carouselFont] || FONT_BY_ID.editorial).css }}
                  >
                    {CAROUSEL_FONTS.map((f) => (
                      <option key={f.id} value={f.id} style={{ fontFamily: f.css }}>
                        {f.label} — {f.meta}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Used for both the live preview and the downloaded PNGs.
                  </p>
                </div>

                {/* Text is composited by Canvas at download — the model never renders letters
                    (gpt-image-1 hallucinates gibberish). No user-facing toggle. */}
                <Button
                  onClick={generateCarousel}
                  disabled={carouselGenerating || !carouselTopic.trim()}
                  className="w-full h-11"
                  data-testid="carousel-generate-btn"
                >
                  {carouselGenerating ? (
                    <span className="inline-flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Crafting {carouselSlideCount} slides…
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      Generate {carouselSlideCount}-slide carousel
                    </span>
                  )}
                </Button>
                {carouselGenerating && (
                  <p className="text-xs text-muted-foreground text-center">
                    Scripts the story arc · generates {carouselSlideCount} cohesive images in parallel · usually 30–60 sec.
                  </p>
                )}
              </div>

              {/* Caption editor */}
              {carouselCaption && (
                <div className="mt-6">
                  <Label className="mb-2 inline-block">Post caption (appears below the carousel)</Label>
                  <Textarea
                    rows={6}
                    value={carouselCaption}
                    onChange={(e) => setCarouselCaption(e.target.value)}
                    data-testid="carousel-caption"
                  />
                </div>
              )}

              {/* Slide editor list */}
              {carouselSlides.length > 0 && (
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label className="block">Slides · click to edit text on any</Label>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={copyAllSlideText}
                        data-testid="carousel-copy-all-btn"
                        title="Copy all slide text (numbered) for manual paste into LinkedIn / IG"
                      >
                        <Copy className="h-3 w-3 mr-1.5" />
                        Copy all slides
                      </Button>
                      <Button
                        size="sm"
                        onClick={copyLinkedInBundle}
                        data-testid="carousel-copy-bundle-btn"
                        title="Caption + every slide as one LinkedIn-ready post — paste once, publish."
                      >
                        <Linkedin className="h-3 w-3 mr-1.5" />
                        Copy LinkedIn post bundle
                      </Button>
                    </div>
                  </div>
                  {/* Slides are an atomic array: regenerated whole, never reordered or per-item mutated.
                      Using index as key is intentional here — there is no stable slide.id from the LLM response. */}
                  {carouselSlides.map((s, i) => (
                    <details
                      key={i}
                      open={i === carouselIdx}
                      className="border border-border rounded-sm bg-background"
                      data-testid={`carousel-slide-${i}`}
                    >
                      <summary
                        className="cursor-pointer px-3 py-2 flex items-center gap-2 text-sm font-semibold"
                        onClick={(e) => { e.preventDefault(); setCarouselIdx(i); }}
                      >
                        <span className="small-caps shrink-0">slide {i + 1}</span>
                        <span className="truncate flex-1">{s.headline}</span>
                        {s.image_base64 ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
                        )}
                      </summary>
                      <div className="px-3 pb-3 space-y-2">
                        <Input
                          value={s.headline}
                          onChange={(e) => editSlide(i, "headline", e.target.value)}
                          placeholder="Slide headline"
                          data-testid={`carousel-slide-headline-${i}`}
                        />
                        <Textarea
                          rows={2}
                          value={s.body}
                          onChange={(e) => editSlide(i, "body", e.target.value)}
                          placeholder="Slide body copy"
                          data-testid={`carousel-slide-body-${i}`}
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copySlideText(s, i)}
                            data-testid={`carousel-slide-copy-${i}`}
                            title="Copy headline + body to clipboard"
                          >
                            <Copy className="h-3 w-3 mr-1" /> Copy text
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => retrySlideImage(i)}
                            disabled={retryingSlide === i}
                            data-testid={`carousel-slide-retry-${i}`}
                          >
                            <RefreshCw className={`h-3 w-3 mr-1 ${retryingSlide === i ? "animate-spin" : ""}`} />
                            {s.image_base64 ? "Regenerate image" : "Retry image"}
                          </Button>
                          {s.image_base64 && (
                            <span className="small-caps">✓ image ready</span>
                          )}
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </section>

            {/* RIGHT — preview + publish */}
            <CarouselPreviewAndPublish
              slides={carouselSlides}
              caption={carouselCaption}
              idx={carouselIdx}
              setIdx={setCarouselIdx}
              selected={selected}
              togglePlatform={togglePlatform}
              status={status}
              post={post}
              posting={posting}
              download={downloadCarousel}
              fontCss={(FONT_BY_ID[carouselFont] || FONT_BY_ID.editorial).css}
              style={carouselStyle}
              citations={carouselCitations}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ─── helpers used in both tabs ───
function CitedFactsPanel({ citations, testId }) {
  if (!citations || citations.length === 0) return null;
  const icon = (t) =>
    t === "stat"       ? "📊"
    : t === "company"  ? "🏢"
    : t === "framework"? "📐"
    : t === "person"   ? "👤"
    : t === "regulation" ? "⚖️"
    : t === "date"     ? "📅"
    : "✓";
  return (
    <div
      className="mt-4 border border-border rounded-sm p-4"
      style={{ background: "var(--karm-ivory-warm, hsl(var(--secondary)))" }}
      data-testid={testId || "cited-facts-panel"}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow inline-flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" />
          Cited facts · verify before posting
        </p>
        <span className="small-caps text-[10px] opacity-60">{citations.length} claim{citations.length === 1 ? "" : "s"}</span>
      </div>
      {/* Citations are returned atomically from /content/generate; never reordered. Index key is intentional. */}
      <ul className="space-y-2">
        {citations.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-sm" data-testid={`cited-fact-${i}`}>
            <span className="text-base leading-snug shrink-0">{icon(c.type)}</span>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-sm leading-snug" style={{ color: "hsl(var(--foreground))" }}>
                {c.value}
              </p>
              <p className="small-caps text-[10px] mt-0.5">
                {c.type}
                <span className="mx-1.5 opacity-50">·</span>
                <a
                  href={`https://www.google.com/search?q=${encodeURIComponent(c.search_query)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:underline underline-offset-4"
                  style={{ color: "hsl(var(--primary))" }}
                  data-testid={`cited-verify-${i}`}
                >
                  verify ↗
                </a>
              </p>
            </div>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-muted-foreground mt-3 italic">
        AI-extracted claims. Always spot-check before publishing under your name.
      </p>
    </div>
  );
}

function SinglePostPreview({ text, imageB64, selected, togglePlatform, status, post, posting, citations }) {
  return (
    <section className="vital-card-static p-6">
      <h2 className="text-lg font-bold mb-4 inline-flex items-center gap-2"><Send className="h-4 w-4" />Publish</h2>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {PLATFORMS.map((p) => {
          const Icon = p.icon;
          const active = selected.includes(p.id);
          const s = status[p.id] || {};
          return (
            <button
              key={p.id}
              onClick={() => togglePlatform(p.id)}
              className={`flex items-center gap-2 px-3 py-3 rounded-sm border text-sm font-semibold ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
              data-testid={`platform-${p.id}`}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1 text-left">{p.label}</span>
              {s.connected
                ? <span className="text-[10px] uppercase tracking-widest text-emerald-500">live</span>
                : <span className="text-[10px] uppercase tracking-widest opacity-70">{p.id === "linkedin" ? "intent" : "mock"}</span>}
            </button>
          );
        })}
      </div>
      <div className="rounded-sm border border-border p-4 bg-muted/40 mb-4">
        <p className="eyebrow mb-2">Preview</p>
        <div className="text-sm whitespace-pre-wrap min-h-20" data-testid="content-preview">
          {text || <span className="text-muted-foreground">Your generated post will appear here.</span>}
        </div>
        {imageB64 && (
          <img src={imageB64.startsWith("data:") ? imageB64 : `data:image/png;base64,${imageB64}`} alt="" className="mt-3 rounded-sm border border-border w-full max-h-64 object-cover" />
        )}
      </div>
      <Button onClick={post} disabled={posting || !text.trim() || selected.length === 0} size="lg" className="w-full" data-testid="content-post-btn">
        {posting ? "Publishing…" : `Publish to ${selected.length} platform${selected.length === 1 ? "" : "s"}`}
      </Button>
      <CitedFactsPanel citations={citations} testId="single-post-citations" />
    </section>
  );
}

function CarouselPreviewAndPublish({ slides, caption, idx, setIdx, selected, togglePlatform, status, post, posting, download, fontCss, citations }) {
  const hasSlides = slides.length > 0;
  const cur = slides[idx];
  const canPost = caption.trim().length > 0 && selected.length > 0;
  return (
    <section className="vital-card-static p-6" data-testid="carousel-preview-pane">
      <h2 className="text-lg font-bold mb-4 inline-flex items-center gap-2"><Send className="h-4 w-4" />Preview & publish</h2>

      {/* Preview slide stack */}
      <div
        className="aspect-square w-full rounded-sm border border-border bg-secondary/40 relative overflow-hidden flex items-center justify-center"
        data-testid="carousel-preview-frame"
      >
        {hasSlides ? (
          cur?.image_base64 ? (
            <>
              <img
                src={cur.image_base64.startsWith("data:") ? cur.image_base64 : `data:image/png;base64,${cur.image_base64}`}
                alt={cur.headline}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid={`carousel-preview-img-${idx}`}
              />
              {/* Slide-number badge — top-right, isolated from body copy so it can never overlap the headline */}
              <div
                className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/55 text-white text-xs font-semibold tracking-wide"
                data-testid={`carousel-preview-badge-${idx}`}
              >
                {idx + 1} / {slides.length}
              </div>
              {/* Headline overlay — shown only when text is NOT baked into the image.
                  When bakeTextInImage=true, the model already rendered the text — overlaying again would duplicate it.
                  Position + alignment + gradient direction all come from slide.text_layout (set by the AI per-slide). */}
              {!cur.bake_text && (() => {
                const L = previewLayout(cur.text_layout);
                if (L.vertical) {
                  // Rotated vertical text band — used sparingly as editorial bookends.
                  const sideClass = L.vertical === "left" ? "left-0" : "right-0";
                  const rotateClass = L.vertical === "left" ? "-rotate-90" : "rotate-90";
                  return (
                    <>
                      <div className={`absolute inset-0 ${L.grad}`} />
                      <div
                        className={`absolute top-1/2 ${sideClass} -translate-y-1/2 ${rotateClass} origin-center p-6 max-w-[80%]`}
                        style={{ fontFamily: fontCss }}
                      >
                        <span className="text-2xl md:text-3xl text-white font-semibold whitespace-nowrap">
                          {cur.headline}
                          {cur.body && <span className="italic text-white/85 ml-3">— {cur.body}</span>}
                        </span>
                      </div>
                    </>
                  );
                }
                return (
                  <div className={`absolute inset-0 flex flex-col ${L.justify} ${L.items} p-6 ${L.grad}`}>
                    <h3
                      className={`text-2xl md:text-3xl text-white leading-tight mb-1 font-semibold ${L.textAlign} max-w-[88%]`}
                      style={{ fontFamily: fontCss }}
                    >
                      {cur.headline}
                    </h3>
                    {cur.body && (
                      <p
                        className={`italic text-sm md:text-base text-white/85 leading-snug ${L.textAlign} max-w-[88%]`}
                        style={{ fontFamily: fontCss }}
                      >
                        {cur.body}
                      </p>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="text-center p-6">
              <AlertCircle className="h-6 w-6 mx-auto mb-2 text-amber-500" />
              <p className="font-serif italic text-sm">Image for this slide didn&apos;t generate.</p>
              <p className="small-caps mt-1">Click &ldquo;Retry image&rdquo; on the slide editor.</p>
            </div>
          )
        ) : (
          <div className="text-center p-6 text-muted-foreground">
            <Layers className="h-8 w-8 mx-auto mb-3 opacity-50" />
            <p className="font-serif italic">Your carousel will appear here.</p>
            <p className="small-caps mt-1">5 cohesive slides · one swipeable story</p>
          </div>
        )}
        {hasSlides && slides.length > 1 && (
          <>
            <button
              onClick={() => setIdx((i) => (i - 1 + slides.length) % slides.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 rounded-sm text-white"
              data-testid="carousel-prev-btn"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % slides.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/40 hover:bg-black/60 rounded-sm text-white"
              data-testid="carousel-next-btn"
              aria-label="Next slide"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {hasSlides && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="carousel-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`carousel-dot-${i}`}
              className="h-1.5 w-6 rounded-sm transition-opacity"
              style={{ background: i === idx ? "hsl(var(--primary))" : "hsl(var(--border))" }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Caption preview */}
      {hasSlides && caption && (
        <div className="mt-4 rounded-sm border border-border p-4 bg-muted/30">
          <p className="eyebrow mb-2">Post caption</p>
          <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="carousel-preview-caption">
            {caption}
          </p>
        </div>
      )}

      <CitedFactsPanel citations={citations} testId="carousel-citations" />

      {/* Publish controls */}
      <div className="mt-5">
        <div className="grid grid-cols-2 gap-2 mb-3">
          {PLATFORMS.map((p) => {
            const Icon = p.icon;
            const active = selected.includes(p.id);
            const s = status[p.id] || {};
            return (
              <button
                key={p.id}
                onClick={() => togglePlatform(p.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-sm border text-sm font-medium ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
                data-testid={`carousel-platform-${p.id}`}
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1 text-left">{p.label}</span>
                {s.connected
                  ? <span className="text-[10px] uppercase tracking-widest text-emerald-500">live</span>
                  : <span className="text-[10px] uppercase tracking-widest opacity-70">{p.id === "linkedin" ? "intent" : "mock"}</span>}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={download}
            disabled={!hasSlides}
            data-testid="carousel-download-btn"
          >
            <Download className="h-4 w-4 mr-1" /> Download all PNGs
          </Button>
          <Button
            onClick={post}
            disabled={posting || !canPost}
            data-testid="carousel-post-btn"
          >
            {posting ? "Publishing…" : `Publish to ${selected.length}`}
          </Button>
        </div>
        {hasSlides && (
          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
            Note: only LinkedIn / IG support multi-image carousels via their official API. We post slide 1 + caption automatically and download all {slides.length} PNGs so you can attach the remaining slides in the platform&apos;s native composer (1 extra click — gives you full Carousel layout control).
          </p>
        )}
      </div>
    </section>
  );
}
